import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import ContactCTA from './components/ContactCTA';
import LiquidEther from 'LiquidEther';

// A variable to hold the timer
let inactivityTimeout;

const loadTawkScript = () => {
  const minimizeChat = () => {
    if (window.Tawk_API) {
      window.Tawk_API.minimize();
    }
  };

  const resetTimer = () => {
    clearTimeout(inactivityTimeout);
    inactivityTimeout = setTimeout(minimizeChat, 30000); // 30s
  };

  if (window.Tawk_API) {
    window.Tawk_API.toggle();
    resetTimer();
    return;
  }

  var Tawk_API = window.Tawk_API || {},
    Tawk_LoadStart = new Date();
  (function () {
    var s1 = document.createElement("script"),
      s0 = document.getElementsByTagName("script")[0];
    s1.async = true;
    s1.src = 'https://embed.tawk.to/68ca3cc7ba858d1923b10eb4/1j5avqvb0';
    s1.charset = 'UTF-8';
    s1.setAttribute('crossorigin', '*');
    s0.parentNode.insertBefore(s1, s0);
  })();

  Tawk_API.onLoad = function () {
    window.Tawk_API.toggle();
    resetTimer();
  };

  document.addEventListener('mousemove', resetTimer);
  document.addEventListener('keydown', resetTimer);
};

const CollaborationStation = () => {
  return (
    <div className="min-h-screen bg-brand-surface">
      <Helmet>
        <title>Get In Touch - RocketFolio</title>
        <meta
          name="description"
          content="Ready to start your next project? Get in touch to discuss how we can work together to bring your ideas to life."
        />
      </Helmet>
      <Header />

      <main className="pt-20 bg-gradient-to-br from-deep-space via-slate-900 to-slate-800">
        {/* Hero Section */}
        <section className="w-full min-h-[80vh] relative overflow-hidden flex items-center">
          {/* Background */}
          <div className="absolute inset-0 pointer-events-none">
            <LiquidEther
              colors={['#5227FF', '#FF9FFC', '#B19EEF']}
              mouseForce={20}
              cursorSize={100}
              viscous={30}
              iterationsViscous={32}
              iterationsPoisson={32}
              resolution={0.5}
              autoDemo={true}
              autoSpeed={0.5}
              autoIntensity={2.2}
              takeoverDuration={0.25}
              autoResumeDelay={3000}
              autoRampDuration={0.6}
            />
          </div>

          <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-16">
            <div className="text-center mb-12">
              <div className="flex justify-center mb-6">
                <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-rocket-fire to-electric-cyan rounded-2xl flex items-center justify-center shadow-brand-lg">
                  <Icon name="MessageCircle" size={28} className="sm:size-32 text-white" />
                </div>
              </div>
              <h1 className="font-headline text-3xl sm:text-4xl lg:text-5xl text-white mb-4">
                Let's Build Something Amazing
              </h1>
              <p className="text-base sm:text-lg lg:text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
                Ready to turn your vision into reality? Whether it's a cutting-edge web
                application, mobile experience, or innovative digital solution, I'm here
                to help bring your ideas to life.
              </p>
            </div>

            {/* Quick Contact Options */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              {/* Email */}
              <a href="mailto:lokendrakumar582809@gmail.com" className="block">
                <div className="bg-white rounded-xl p-6 text-center shadow-brand border border-border hover:shadow-brand-lg transition-all h-full flex flex-col justify-between">
                  <div>
                    <div className="w-12 h-12 bg-electric-cyan/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                      <Icon name="Mail" size={24} className="text-electric-cyan" />
                    </div>
                    <h3 className="font-headline text-lg text-deep-space mb-2">Email Me</h3>
                    <p className="text-slate-600 mb-3 text-sm sm:text-base">
                      Get a detailed response within 24 hours
                    </p>
                  </div>
                  <button className="text-electric-cyan hover:text-electric-cyan/80 font-medium mt-2">
                    Send Mail
                  </button>
                </div>
              </a>

              {/* Call */}
              <a
                href="https://calendly.com/lokendrakumar582809/30min"
                className="block"
              >
                <div className="bg-white rounded-xl p-6 text-center shadow-brand border border-border hover:shadow-brand-lg transition-all h-full flex flex-col justify-between">
                  <div>
                    <div className="w-12 h-12 bg-trust-green/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                      <Icon name="Phone" size={24} className="text-trust-green" />
                    </div>
                    <h3 className="font-headline text-lg text-deep-space mb-2">
                      Schedule Call
                    </h3>
                    <p className="text-slate-600 mb-3 text-sm sm:text-base">
                      30-minute discovery session
                    </p>
                  </div>
                  <button className="text-trust-green hover:text-trust-green/80 font-medium mt-2">
                    Book Meeting
                  </button>
                </div>
              </a>

              {/* Chat */}
              <button
                onClick={loadTawkScript}
                className="block text-left w-full"
              >
                <div className="bg-white rounded-xl p-6 text-center shadow-brand border border-border hover:shadow-brand-lg transition-all h-full flex flex-col justify-between">
                  <div>
                    <div className="w-12 h-12 bg-rocket-fire/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                      <Icon name="MessageSquare" size={24} className="text-rocket-fire" />
                    </div>
                    <h3 className="font-headline text-lg text-deep-space mb-2">Live Chat</h3>
                    <p className="text-slate-600 mb-3 text-sm sm:text-base">
                      Quick questions? I'm online
                    </p>
                  </div>
                  <span className="text-rocket-fire hover:text-rocket-fire/80 font-medium mt-2">
                    Start Chat
                  </span>
                </div>
              </button>
            </div>
          </div>
        </section>

        <ContactCTA />
      </main>

      {/* Footer */}
      <footer className="bg-deep-space text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-3 sm:space-y-0 sm:space-x-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-br from-rocket-fire to-electric-cyan rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">P</span>
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-headline text-sky-500">
                  PortFolio
                </h3>
                <p className="text-xs text-slate-400">Ready To Developer</p>
              </div>
            </div>

            <p className="text-slate-400 mb-6 max-w-xl mx-auto text-sm sm:text-base">
              Ready to launch into the next phase of my career. Let's build something
              amazing together.
            </p>

            <div className="border-t border-slate-700 pt-6">
              <p className="text-slate-500 text-xs sm:text-sm">
                Crafted with passion and precision by Lokendra Kumar.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CollaborationStation;
